export interface KeyboardActions {
  nextNumber: () => void;
  toggleAutoPlay: () => void;
  openSettings: () => void;
  confirmReset: () => void;
  closeModalOrSkip: () => void;
  showHelp: () => void;
}

export class KeyboardHandler {
  private actions: KeyboardActions | null = null;
  private enabled: boolean = false;
  private isModalOpen: () => boolean = () => false;
  private isPlaying: () => boolean = () => false;
  private isAudioPlaying: () => boolean = () => false;
  private isComplete: () => boolean = () => false;
  private boundHandler: ((e: KeyboardEvent) => void) | null = null;

  initialize(
    actions: KeyboardActions,
    guards: {
      isModalOpen: () => boolean;
      isPlaying: () => boolean;
      isAudioPlaying: () => boolean;
      isComplete: () => boolean;
    }
  ): void {
    this.actions = actions;
    this.isModalOpen = guards.isModalOpen;
    this.isPlaying = guards.isPlaying;
    this.isAudioPlaying = guards.isAudioPlaying;
    this.isComplete = guards.isComplete;

    this.boundHandler = (e: KeyboardEvent) => this.handleKeyDown(e);
    document.addEventListener('keydown', this.boundHandler);
    this.enabled = true;
  }

  enable(): void {
    this.enabled = true;
  }

  disable(): void {
    this.enabled = false;
  }

  destroy(): void {
    if (this.boundHandler) {
      document.removeEventListener('keydown', this.boundHandler);
      this.boundHandler = null;
    }
  }

  private handleKeyDown(e: KeyboardEvent): void {
    if (!this.enabled || !this.actions) return;

    // Suppress in input elements
    const target = e.target as HTMLElement;
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLSelectElement ||
      target instanceof HTMLTextAreaElement
    ) {
      return;
    }

    const key = e.key.toLowerCase();
    const keyCode = e.key;

    switch (key) {
      case 'n':
      case ' ':
        // Allow N/Space in IDLE or PLAYING state, but not when audio is playing
        if (!this.isModalOpen() && !this.isAudioPlaying()) {
          e.preventDefault();
          this.actions.nextNumber();
          this.flashButton('next-btn');
        }
        break;

      case 'a':
        if (!this.isModalOpen() && !this.isComplete()) {
          e.preventDefault();
          this.actions.toggleAutoPlay();
          this.flashButton('autoplay-toggle');
        }
        break;

      case 's':
        if (!this.isModalOpen()) {
          e.preventDefault();
          this.actions.openSettings();
          this.flashButton('settings-btn');
        }
        break;

      case 'r':
        if (!this.isModalOpen()) {
          e.preventDefault();
          this.actions.confirmReset();
          this.flashButton('reset-btn');
        }
        break;

      case 'escape':
        e.preventDefault();
        this.actions.closeModalOrSkip();
        break;
    }

    // Handle ? key (can be Shift+/ or ? depending on keyboard layout)
    if (keyCode === '?' || (e.shiftKey && (keyCode === '/' || key === '/'))) {
      e.preventDefault();
      this.actions.showHelp();
    }
  }

  private flashButton(id: string): void {
    const btn = document.getElementById(id);
    if (!btn) return;
    btn.classList.add('keyboard-flash');
    setTimeout(() => btn.classList.remove('keyboard-flash'), 100);
  }
}
